<template>
  <div>
    <h3 v-if="!isDisplayInline">Actions</h3>
    <div class="d-flex flex-wrap" :class="{'align-items-start flex-column': !isDisplayInline}" style="gap: 10px 5px;">
      <button
        class="btn btn-danger btn-sm"
        @click="openUserBanModal(2)"
        type="button"
      >
        Ban
      </button>
      <button
          class="btn text-white user-status-3 text-nowrap btn-sm"
          @click="openUserBanModal(3)"
          type="button"
      >
        Shadow Ban
      </button>
      <button
        class="btn text-white user-status-6 text-nowrap btn-sm"
        @click="openUserBanModal(6)"
        type="button"
      >
        On Hold
      </button>
      <button v-if="user.status !== 4"
        class="btn btn-secondary btn-sm"
        @click="openReviewConfirmModal"
        type="button"
      >
        Review
      </button>
      <button v-if="can_require_facetec && hasPermission('require_facetec_verification')"
        class="btn btn-info text-nowrap btn-sm"
        @click="openRequireFacetecModal"
        :disabled="isFacetecPending"
        type="button"
      >
        Require FaceTec Verification
      </button>
      <button v-if="isCanUpdateGoodwillCredit"
        class="btn btn-success btn-sm"
        @click="openGoodwillCreditModal"
        type="button"
      >
        Credit
      </button>
      <button
        class="btn btn-primary text-nowrap btn-sm"
        @click="openLiveQuerySurveysModal"
        type="button"
      >
        Surveys
      </button>
      <b-button class="btn-sm" variant="primary" @click="openDBViewModal">DB View</b-button>
      <b-button
        variant="outline-primary"
        class="btn-sm"
        @click="openPersonalDataModal"
      >
        Personal Data
      </b-button>

      <b-button
        variant="primary"
        class="btn-sm"
        @click="openVerisoulSessionsModal"
      >
        Verisoul Sessions
      </b-button>
      <button
        v-if="hasPermission('users_delete') && !user.is_anonymized"
        class="btn btn-danger btn-sm text-nowrap"
        type="button"
        :disabled="isDeletingUser"
        @click="deleteUser"
      >
        Delete
      </button>
      <button
        v-if="hasPermission('users_delete') && user.is_deletion_pending && !justRestored"
        class="btn btn-success btn-sm text-nowrap"
        type="button"
        :disabled="isRestoringUser"
        @click="restoreUser"
      >
        Restore
      </button>
    </div>

    <UserBanModal
      :isNeedApproval="false"
      :userId="user.id"
      :status="currentStatusBan"
      :suppress-status-change-error-toast="isSupportReviewRoute"
      ref="user_ban_form"
      @added="addedUserBanned"
      @error="handleStatusChangeError"
    />
    <ConfirmReviewModal
      ref="confirm_review_modal"
      :userId="user.id"
      :suppress-status-change-error-toast="isSupportReviewRoute"
      @changed="changeToReviewStatus"
      @error="handleStatusChangeError"
    />
    <RequireFacetecModal ref="require_facetec_modal" :userId="user.id" @changed="onFacetecRequired" />
    <GoodwillCreditModal ref="goodwill_credit_modal" :exchangeRate="exchangeRate" :currencyName="currencyName" :userId="user.id" :maxCreditAmount="maxCreditAmount" @type-changed="onGoodwillTypeChanged" />
    <LiveQuerySurveysModal ref="liveQuerySurveysModal" :user="user" :userId="user.id" />
    <DBViewModal ref="dbViewModal" :id="user.id" :data="dbViewData" />
    <PersonalDataModal ref="personalDataModal" :id="user.id" :data="personalData" />
    <VerisoulSessionsModal ref="verisoulSessionsModal" :id="user.id" :data="verisoulSessionsData" />

    <b-modal id="modal-db-view" ref="modalDBView" size="lg" hide-footer title="Database View">
      <table class="table table-striped">
        <thead>
        <tr>
          <th>Column</th>
          <th>Value</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(value, key) in dbViewData" :key="key">
          <td>{{ key }}</td>
          <td style="word-break: break-word; white-space: pre-wrap;">{{ value }}</td>
        </tr>
        </tbody>
      </table>
    </b-modal>
  </div>
</template>

<script>
import ConfirmReviewModal from '@/views/users/UserDetails/ConfirmReviewModal.vue'
import RequireFacetecModal from '@/views/users/UserDetails/RequireFacetecModal.vue'
import UserBanModal from '@/views/users/UserDetails/UserBanModal.vue'
import GoodwillCreditModal from '@/views/users/UserDetails/GoodwillCreditModal.vue'
import LiveQuerySurveysModal from '@/views/users/UserDetails/LiveQuerySurveysModal.vue'
import PersonalDataModal from '@/views/users/UserDetails/PersonalDataModal.vue'
import VerisoulSessionsModal from '@/views/users/UserDetails/VerisoulSessionsModal.vue'
import DBViewModal from '@/main/custom-components/DBViewModal.vue'
import { isDifferentAdminAssignedError } from '@/utils/helpers'

export default {
  name: 'UserActionsBox',
  components: {
    DBViewModal,
    LiveQuerySurveysModal,
    GoodwillCreditModal,
    UserBanModal,
    ConfirmReviewModal,
    RequireFacetecModal,
    PersonalDataModal,
    VerisoulSessionsModal
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    can_trigger_verification: {
      type: Boolean
    },
    phone_verifications: {
      type: Array
    },
    can_require_facetec: {
      type: Boolean,
      default: false
    },
    trueface_status: {
      type: Number,
      default: null
    },
    isDisplayInline: {
      type: Boolean,
      default: false
    },
    quality: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      exchangeRate: null,
      currencyName: 'Points',
      currentStatusBan: 2,
      dbViewData: {},
      personalData: {},
      verisoulSessionsData: [],
      isDeletingUser: false,
      isRestoringUser: false,
      justRestored: false
    }
  },
  computed: {
    isFacetecPending () {
      return this.trueface_status === 1
    },
    isCanUpdateGoodwillCredit () {
      return this.canAccessPageByPermissionModules(['internal.user.update_goodwill_credit'])
    },
    maxCreditAmount () {
      if (this.canAccessPageByPermissionModules(['internal.user.update_goodwill_credit_max_300'])) {
        return 300
      }
      return this.canAccessPageByPermissionModules(['internal.user.update_goodwill_credit_max_50']) ? 50 : 10
    },
    isSupportReviewRoute () {
      return this.$route.path === '/support-user-reviews'
    }
  },
  methods: {
    openUserBanModal (status) {
      this.currentStatusBan = status
      this.getUserBanFormVue().showForm()
    },
    openReviewConfirmModal () {
      this.getConfirmReviewModalVue().showForm()
    },
    openRequireFacetecModal () {
      this.$refs.require_facetec_modal.show()
    },
    openGoodwillCreditModal () {
      this.fetchExchangeRate('surveys')
        .then(() => {
          this.getGoodwillCreditModalVue().showForm()
        })
        .catch(() => {
          // error already surfaced in fetchExchangeRate
        })
    },
    // Triggered by the modal's Type dropdown. Re-fetch the rate for the chosen
    // type so the displayed exchange rate / user reward reflect surveys vs offers.
    // offer_id stays null for OP-496 v1 (app-level offers rate, no offer picker).
    onGoodwillTypeChanged ({ type, offer_id: offerId = null }) {
      this.fetchExchangeRate(type, offerId).catch(() => {
        // error already surfaced in fetchExchangeRate
      })
    },
    fetchExchangeRate (type = 'surveys', offerId = null) {
      this.showLoading()
      // Guard against out-of-order responses: rapid Surveys/Offers toggles can
      // overlap (the loading overlay sits below the modal, so the select stays
      // clickable). Only the latest request may update the displayed rate.
      const requestId = (this.exchangeRateRequestId || 0) + 1
      this.exchangeRateRequestId = requestId
      const params = { type }
      if (offerId !== null && offerId !== undefined) {
        params.offer_id = offerId
      }
      return this.$http.get(`/api/apps/${this.user.app_id}/exchange-rate`, { params })
        .then((response) => {
          if (requestId !== this.exchangeRateRequestId) return

          this.exchangeRate = response.data.exchange_rate
          this.currencyName = response.data.currency_name
        }).catch((errors) => {
          if (requestId === this.exchangeRateRequestId && errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
          throw errors
        }).finally(() => {
          if (requestId === this.exchangeRateRequestId) {
            this.hideLoading()
          }
        })
    },
    openLiveQuerySurveysModal () {
      this.$refs.liveQuerySurveysModal.showModal()
    },
    openSurveysDashboard () {
      const url = this.user && this.user.panel_url
      if (!url) {
        return
      }
      window.open(url, '_blank')
    },
    getUserBanFormVue () {
      return this.$refs.user_ban_form
    },
    getConfirmReviewModalVue () {
      return this.$refs.confirm_review_modal
    },
    getGoodwillCreditModalVue () {
      return this.$refs.goodwill_credit_modal
    },
    async addedUserBanned (data) {
      if ('/support-user-reviews' === this.$route.path) {
        this.$emit('update:user', data.response)
        return
      }

      this.showLoading()
      try {
        const response = await this.$http.get(`/api/user/${data.response.id}`)
        this.$emit('update:user', response.data.user)
      } catch (error) {
        // Do nothing on error
      } finally {
        this.hideLoading()
      }
    },
    async changeToReviewStatus (data) {
      const newPhoneVerifications = [
        {phone: null, verified: false},
        ...this.phone_verifications
      ]

      this.$emit('update:can_trigger_verification', false)
      this.$emit('update:phone_verifications', newPhoneVerifications)

      if ('/support-user-reviews' === this.$route.path) {
        this.$emit('update:user', data.response)
        return
      }

      this.showLoading()
      try {
        const response = await this.$http.get(`/api/user/${data.response.id}`)
        this.$emit('update:user', response.data.user)
      } catch (error) {
        // Do nothing on error
      } finally {
        this.hideLoading()
      }
    },
    handleStatusChangeError (error) {
      if (!this.isSupportReviewRoute || !isDifferentAdminAssignedError(error)) {
        return
      }

      this.alertWarning('This review was picked up by another admin')
      this.$emit('unavailable-review')
    },
    onFacetecRequired () {
      this.$emit('facetec-required')
    },
    openDBViewModal () {
      this.showLoading()

      this.$http
        .get(`/api/user/${this.user.id}/database-data`)
        .then((response) => {
          this.hideLoading()
          this.dbViewData = response.data

          this.$refs.dbViewModal.showModal()
        })
        .catch((errors) => {
          this.hideLoading()
          this.alertError(errors.response.data)
        })
    },
    openPersonalDataModal () {
      this.showLoading()

      this.$http
        .get(`/api/user/${this.user.id}/personal-data`)
        .then((response) => {
          this.hideLoading()

          // accept both shapes
          this.personalData = response?.data?.data || response?.data || {}

          // call the method that exists on the child
          this.$refs.personalDataModal.showModal()
        })
        .catch((error) => {
          this.hideLoading()
          const payload = error?.response?.data || error?.message || 'Failed to load personal data.'
          this.alertError(payload)
        })
    },
    openVerisoulSessionsModal () {
      this.verisoulSessionsData = this.quality?.verisoul_sessions || []
      this.$refs.verisoulSessionsModal.showModal()
    },
    async deleteUser () {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this user\'s data? This action cannot be undone.',
        {
          title: 'Delete User Data',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true
        }
      )

      if (!confirmed) {
        return
      }

      this.isDeletingUser = true
      try {
        const response = await this.$http.delete(`/api/users/${this.user.id}/anonymize`)
        this.alertSuccess(response?.data?.message || 'User data deleted successfully.')
        this.showLoading()
        try {
          const fresh = await this.$http.get(`/api/user/${this.user.id}`)
          this.$emit('update:user', fresh.data.user)
        } catch {
          // Refresh failed — delete was still successful; silently ignore
        }
      } catch (error) {
        this.alertError(error?.response?.data?.errors || error?.response?.data?.message || error?.response?.data || error.message)
      } finally {
        this.isDeletingUser = false
        this.hideLoading()
      }
    },
    async restoreUser () {
      const scheduledAt = this.$options.filters.dotDateTime(this.user.deletion_scheduled_at)
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `This user is scheduled for deletion on ${scheduledAt}. Restoring cancels that request and brings the account back under its original external ID.`,
        {
          title: 'Restore User',
          size: 'sm',
          okVariant: 'success',
          okTitle: 'Restore',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true
        }
      )

      if (!confirmed) {
        return
      }

      this.isRestoringUser = true
      try {
        const response = await this.$http.post(`/api/users/${this.user.id}/restore-deletion`)
        // Admin reads go through MaxScale, so the refetch below can hit a replica that has not
        // caught up yet and hand back a user who still looks pending. The flag covers the case
        // where the refetch itself fails and no fresh user is emitted at all.
        this.justRestored = true
        this.alertSuccess(response?.data?.message || 'User restored successfully.')
        this.showLoading()
        try {
          const fresh = await this.$http.get(`/api/user/${this.user.id}`)
          // Correct the pending state on the way out rather than at each consumer: the restore
          // has already succeeded, and the badge in UserDetails.vue reads the same object.
          this.$emit('update:user', {
            ...fresh.data.user,
            is_deletion_pending: false,
            deletion_scheduled_at: null
          })
        } catch {
          // Refresh failed — restore was still successful; silently ignore
        }
      } catch (error) {
        this.alertError(error?.response?.data?.errors || error?.response?.data?.message || error?.response?.data || error.message)
      } finally {
        this.isRestoringUser = false
        this.hideLoading()
      }
    }
  }
}
</script>
